import React, { createContext, useContext, useEffect, useState } from 'react';

import { LATEST_API_VERSION } from '@shopify/shopify-api';
import Client from 'shopify-buy';
import type { Checkout } from 'shopify-buy';

import type { CartItemObject } from '../utility/interfaces';

interface StoreContextObject {
    addVariantToCart: (
        product: CartItemObject,
        variantId: string,
        quantity: string | number
    ) => void;
    checkout: Checkout | null;
    client: Client;
    didJustAddToCart: boolean;
    isIniting: boolean;
    isLoading: boolean;
    lastAdded: CartItemObject | null;
    removeLineItem: (checkoutId: string, lineItemId: string) => void;
    updateLineItem: (checkoutId: string, lineItemId: string, quantity: string | number) => void;
}

const client = Client.buildClient({
    apiVersion: LATEST_API_VERSION,
    domain: 'shop.bullshot.co.uk',
    storefrontAccessToken: '9fc6596f3ff1331b915e29ac2b0039ff',
});

const defaultValues: StoreContextObject = {
    addVariantToCart: () => {},
    checkout: null,
    client,
    didJustAddToCart: false,
    isIniting: true,
    isLoading: false,
    lastAdded: null,
    removeLineItem: () => {},
    updateLineItem: () => {},
};

const StoreContext = createContext(defaultValues);

const isBrowser = typeof window !== `undefined`;
const localStorageKey = `shopify_checkout_id`;

interface StoreProviderProps {
    children: React.ReactNode;
}

export const StoreProvider = ({ children }: StoreProviderProps) => {
    const [checkout, setCheckout] = useState<Checkout | null>(defaultValues.checkout);
    const [isIniting, setIsIniting] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [didJustAddToCart, setDidJustAddToCart] = useState(false);
    const [lastAdded, setLastAdded] = useState<CartItemObject | null>(null);

    const setCheckoutItem = (checkout: Checkout) => {
        if (isBrowser) {
            localStorage.setItem(localStorageKey, checkout.id.toString() || '');
        }

        setCheckout(checkout);
    };

    useEffect(() => {
        const initializeCheckout = async () => {
            const existingCheckoutID = isBrowser ? localStorage.getItem(localStorageKey) : null;

            if (existingCheckoutID && existingCheckoutID !== '') {
                try {
                    const existingCheckout = await client.checkout.fetch(existingCheckoutID);
                    if (!existingCheckout.completedAt) {
                        setCheckoutItem(existingCheckout);
                        setIsIniting(false);
                        return;
                    }
                } catch (e) {
                    localStorage.setItem(localStorageKey, '');
                }
            }

            const newCheckout = await client.checkout.create();

            setCheckoutItem(newCheckout);
            setIsIniting(false);
        };

        initializeCheckout();
    }, []);

    const addVariantToCart = async (
        product: CartItemObject,
        variantId: string,
        quantity: string | number
    ) => {
        setIsLoading(true);

        const checkoutID = checkout?.id;

        const lineItemsToUpdate = [
            {
                variantId,
                quantity: parseInt(quantity.toString(), 10),
            },
        ];

        const response = await client.checkout.addLineItems(checkoutID || '', lineItemsToUpdate);

        setCheckout(response);
        setIsLoading(false);

        setDidJustAddToCart(true);
        setLastAdded(product);

        setTimeout(() => {
            setDidJustAddToCart(false);
            setLastAdded(null);
        }, 1000);
    };

    const removeLineItem = async (checkoutID: string, lineItemID: string) => {
        setIsLoading(true);

        const response = await client.checkout.removeLineItems(checkoutID, [lineItemID]);

        setCheckout(response);
        setIsLoading(false);
    };

    const updateLineItem = async (
        checkoutID: string,
        lineItemID: string,
        quantity: string | number
    ) => {
        setIsLoading(true);

        const lineItemsToUpdate = [{ id: lineItemID, quantity: parseInt(quantity.toString(), 10) }];

        const response = await client.checkout.updateLineItems(checkoutID, lineItemsToUpdate);

        setCheckout(response);
        setIsLoading(false);
    };

    return (
        <StoreContext.Provider
            value={{
                ...defaultValues,
                addVariantToCart,
                removeLineItem,
                updateLineItem,
                checkout,
                isIniting,
                isLoading,
                didJustAddToCart,
                lastAdded,
            }}
        >
            {children}
        </StoreContext.Provider>
    );
};

const useStore = () => useContext(StoreContext);

export default useStore;
